import React, { useEffect, useMemo, useState } from "react";
import DateParse from "../../../components/DateParse";
import {
  CloseIcon,
  CompareIcon,
  SearchIcon,
  VersionsIcon,
} from "../../Common/SvgIcons/WorkflowIcons";

const PAGE_SIZE_OPTIONS = [6, 10, 20];
const FALLBACK_COLUMNS = [
  { Header: "Version", accessor: "version" },
  { Header: "Model Name", accessor: "name" },
  { Header: "Framework", accessor: "framework" },
  { Header: "Task Type", accessor: "task_type" },
  { Header: "Benchmark Dataset", accessor: "benchmark_dataset" },
  { Header: "Formats", accessor: "model_graph_format" },
  { Header: "Source", accessor: "import_source" },
  { Header: "Updated", accessor: "updated_on" },
];

const formatVersion = (version) => {
  if (version == null || version === "") return "—";
  const value = String(version);
  return value.toLowerCase().startsWith("v") ? value : `v${value}`;
};

const compareVersionsDescending = (leftVersion, rightVersion) => {
  const leftParts = String(leftVersion || "").match(/\d+/g)?.map(Number) || [];
  const rightParts = String(rightVersion || "").match(/\d+/g)?.map(Number) || [];
  const partCount = Math.max(leftParts.length, rightParts.length);

  for (let index = 0; index < partCount; index += 1) {
    const difference = (rightParts[index] || 0) - (leftParts[index] || 0);
    if (difference !== 0) return difference;
  }
  return 0;
};

const formatLabel = (value) => {
  if (value == null || value === "") return "—";
  return String(value)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
};

const isObjectsOfInterestColumn = (column) =>
  [column?.Header, column?.accessor].some(
    (value) =>
      String(value || "")
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "") === "objectsofinterest"
  );

const getClassCount = (model) => {
  const classes = model?.config?.exp_config?.data?.class_names;
  return Array.isArray(classes) ? classes.length : 0;
};

const formatCellValue = (model, accessor) => {
  const value = model?.[accessor];

  if (accessor === "version") return formatVersion(value);
  if (accessor === "created_on" || accessor === "updated_on") {
    return DateParse(value) || "—";
  }
  if (accessor === "class_count" || accessor === "classes") {
    return value ?? getClassCount(model);
  }
  if (Array.isArray(value)) {
    return value.length
      ? value.map((item) => String(item).toUpperCase()).join(", ")
      : "—";
  }
  if (value == null || value === "") return "—";
  if (["framework", "task_type", "import_source"].includes(accessor)) {
    return formatLabel(value);
  }
  return String(value);
};

export default function ModelVersionsTable({
  columns = [],
  rows = [],
  searchValue,
  onSearchChange,
  compareMode,
  showComparison,
  canCompareVersions = false,
  selectedVersionIds,
  onCompare,
  onCancelCompare,
  onToggleVersion,
  onOpenVersion,
}) {
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [pageIndex, setPageIndex] = useState(0);
  const visibleColumns = columns.length ? columns : FALLBACK_COLUMNS;

  const sortedRows = useMemo(
    () =>
      [...rows].sort((left, right) => {
        const versionDifference = compareVersionsDescending(
          left.version,
          right.version
        );
        if (versionDifference !== 0) return versionDifference;
        return new Date(right.updated_on || right.created_on || 0) -
          new Date(left.updated_on || left.created_on || 0);
      }),
    [rows]
  );

  const pageCount = Math.max(1, Math.ceil(sortedRows.length / pageSize));
  const currentPage = Math.min(pageIndex, pageCount - 1);
  const visibleRows = sortedRows.slice(
    currentPage * pageSize,
    currentPage * pageSize + pageSize
  );

  useEffect(() => {
    setPageIndex(0);
  }, [rows, pageSize]);

  return (
    <div className="container-card model-version-history">
      <div className="model-version-history__header">
        <div className="model-version-history__title">
          <VersionsIcon className="wf-icon" />
          <span>Version History</span>
        </div>

        <div className="model-version-history__actions">
          {canCompareVersions && (
            <div
              className={`model-versions-compare-control ${
                compareMode || showComparison ? "is-active" : ""
              }`}
            >
              <button
                type="button"
                className="model-versions-compare-control__action"
                onClick={onCompare}
              >
                <CompareIcon className="model-versions-toolbar-icon" />
                <span>
                  {selectedVersionIds.length
                    ? `Compare Versions (${selectedVersionIds.length})`
                    : "Compare Versions"}
                </span>
              </button>
              {(compareMode || showComparison) && (
                <>
                  <div className="model-versions-compare-control__divider" />
                  <button
                    type="button"
                    className="model-versions-compare-control__close"
                    onClick={onCancelCompare}
                    aria-label="Cancel compare selection"
                    title="Cancel compare"
                  >
                    <CloseIcon className="model-versions-toolbar-icon" />
                  </button>
                </>
              )}
            </div>
          )}

          {canCompareVersions && (
            <div className="model-version-history__search">
              <SearchIcon className="model-versions-toolbar-icon" />
              <input
                type="search"
                placeholder="Filter Versions"
                aria-label="Filter model versions"
                value={searchValue}
                onChange={(event) => onSearchChange(event.target.value)}
              />
            </div>
          )}
        </div>
      </div>

      <div className="model-version-history__table-wrap">
        <table className="model-version-history__table">
          <thead>
            <tr>
              {compareMode && <th className="model-version-history__check" scope="col" />}
              {visibleColumns.map((column) => (
                <th
                  key={column.accessor}
                  scope="col"
                  className={isObjectsOfInterestColumn(column) ? "model-version-history__objects-heading" : undefined}
                >
                  {column.Header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((model) => {
              const isSelected = selectedVersionIds.includes(model._id);
              return (
                <tr
                  key={model._id}
                  className={isSelected ? "is-selected" : undefined}
                  onClick={() =>
                    compareMode ? onToggleVersion(model) : onOpenVersion(model)
                  }
                >
                  {compareMode && (
                    <td className="model-version-history__check">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => onToggleVersion(model)}
                        onClick={(event) => event.stopPropagation()}
                        aria-label={`Select ${formatVersion(model.version)} for comparison`}
                      />
                    </td>
                  )}
                  {visibleColumns.map((column) => {
                    const isObjectsColumn = isObjectsOfInterestColumn(column);
                    const cellClassName = [
                      column.accessor === "name" ? "model-version-history__name" : "",
                      isObjectsColumn ? "model-version-history__objects" : "",
                    ].filter(Boolean).join(" ") || undefined;

                    return (
                      <td
                        key={column.accessor}
                        className={cellClassName}
                        title={column.accessor === "name" ? model.name : undefined}
                      >
                        {column.accessor === "version" ? (
                          <button
                            type="button"
                            className="model-version-history__version"
                            onClick={(event) => {
                              event.stopPropagation();
                              compareMode ? onToggleVersion(model) : onOpenVersion(model);
                            }}
                          >
                            {formatCellValue(model, column.accessor)}
                          </button>
                        ) : isObjectsColumn ? (
                          <div className="model-version-history__objects-scroll">
                            <span className="model-version-history__objects-value">
                              {formatCellValue(model, column.accessor)}
                            </span>
                          </div>
                        ) : (
                          formatCellValue(model, column.accessor)
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            {visibleRows.length === 0 && (
              <tr>
                <td
                  colSpan={visibleColumns.length + (compareMode ? 1 : 0)}
                  className="model-version-history__empty"
                >
                  {searchValue.trim()
                    ? `No versions match “${searchValue.trim()}”.`
                    : "No versions available."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="model-version-history__pagination">
        <label>
          Rows per page:
          <select
            value={pageSize}
            onChange={(event) => setPageSize(Number(event.target.value))}
          >
            {PAGE_SIZE_OPTIONS.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </label>
        <div>
          <button
            type="button"
            disabled={currentPage === 0}
            onClick={() => setPageIndex((page) => Math.max(0, page - 1))}
            aria-label="Previous page"
          >
            ‹
          </button>
          <span>{currentPage + 1} / {pageCount}</span>
          <button
            type="button"
            disabled={currentPage >= pageCount - 1}
            onClick={() => setPageIndex((page) => Math.min(pageCount - 1, page + 1))}
            aria-label="Next page"
          >
            ›
          </button>
        </div>
      </div>
    </div>
  );
}
